import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Lock, Eye, Mail, Phone, MapPin, ArrowLeft } from "lucide-react";
import StickyNavigation from "@/components/layout/StickyNavigation";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;

  const navigateToPage = (path: string) => {
    navigate(tLink(path));
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-white">
      <StickyNavigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: April 26, 2018
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Information Collection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Eye className="h-6 w-6 mr-3 text-blue-600" />
              What information do we collect?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              We collect information from you when you register on our site, place an order, subscribe to our newsletter, respond to a survey or fill out a form.
            </p>
            <p>
              When ordering or registering on our site, as appropriate, you may be asked to enter your: name, e-mail address or phone number. You may, however, visit our site anonymously. However, any information required to provide an insurance quote or issue a policy will be collected and securely stored.
            </p>
            <p>
              Yallacompare only collects the minimum necessary data to facilitate insurance quotes, policies, renewals, and communication lines with the client on these subjects.
            </p>
          </CardContent>
        </Card>

        {/* Information Usage */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">What do we use your information for?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>Any of the information we collect from you may be used in one of the following ways:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>To improve our website</strong> - we continually strive to improve our website offerings based on the information and feedback we receive from you
              </li>
              <li>
                <strong>To improve customer service</strong> - your information helps us to more effectively respond to your customer service requests and support needs
              </li>
              <li>
                <strong>To process transactions</strong> - We may share or sell information with third parties for marketing or other purposes without your consent
              </li>
              <li>
                <strong>To administer a contest, promotion, survey or other site feature</strong>
              </li>
              <li>
                <strong>To send periodic emails</strong> - The email address you provide for order processing, may be used to send you information and updates pertaining to your order, in addition to receiving occasional company news, updates, related product or service information, etc.
              </li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg mt-4">
              <p className="text-sm">
                <strong>Note:</strong> If at any time you would like to unsubscribe from receiving future emails, simply email business@yallacompare.com
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Information Protection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Lock className="h-6 w-6 mr-3 text-green-600" />
              How do we protect your information?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              We implement a variety of security measures to maintain the safety of your personal information when you place an order or enter, submit, or access your personal information.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">SSL Encryption</h4>
                <p className="text-sm text-green-700">All sensitive information is transmitted via Secure Socket Layer (SSL) technology and encrypted in our database.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Web Application Firewall</h4>
                <p className="text-sm text-blue-700">All interactions are protected through our Web Application Firewall against cyber-attacks.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-800 mb-2">Private Network Storage</h4>
                <p className="text-sm text-purple-700">Customer data is stored and encrypted within a private network with latest security patches.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">Access Control</h4>
                <p className="text-sm text-orange-700">Strict IAM rules and Principle of Least Privilege (POLP) for all access.</p>
              </div>
            </div>
            <p className="mt-4">
              Data is automatically backed up to ensure no losses in the event of a server failure.
            </p>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Do we use cookies?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              Yes. Cookies are small files that a site or its service provider transfers to your computer's hard drive through your Web browser (if you allow) that enables the sites or service provider's systems to recognize your browser and capture and remember certain information.
            </p>
            <p>
              We use cookies to understand and save your preferences for future visits, keep track of advertisements and compile aggregate data about site traffic and site interaction so that we can offer better site experiences and tools in the future.
            </p>
          </CardContent>
        </Card>

        {/* Information Disclosure */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Do we disclose any information to outside parties?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              Yallacompare takes user privacy seriously, we will refrain from releasing your personally identifiable information to third parties. However, this information may be provided to trusted insurance companies to facilitate the quote and issuance of policies only. Under these circumstances, these third parties are contractually agreed to keep this information confidential.
            </p>
          </CardContent>
        </Card>

        {/* Third Party Links */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Third party links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              Occasionally, at our discretion, we may include or offer third party products or services on our website. These third party sites have separate and independent privacy policies. We therefore have no responsibility or liability for the content and activities of these linked sites. Nonetheless, we seek to protect the integrity of our site and welcome any feedback about these sites.
            </p>
            <p>
              Because we value your privacy we have taken the necessary precautions to be in compliance with the California Online Privacy Protection Act. We therefore will not distribute your personal information to outside parties without your consent.
            </p>
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Terms and Conditions</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              Please also visit our Terms and Conditions section establishing the use, disclaimers, and limitations of liability governing the use of our website at yallacompare.com/terms.
            </p>
          </CardContent>
        </Card>

        {/* Consent */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Your Consent</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>By using our site, you consent to our privacy policy.</p>
          </CardContent>
        </Card>

        {/* Changes to Policy */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Changes to our Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              If we decide to change our privacy policy, we will post those changes on this page, and/or update the Privacy Policy modification date below.
            </p>
            <p className="font-semibold mt-2">This policy was last modified on 26/04/2018.</p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-8 bg-blue-600 text-white">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Contacting Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>If there are any questions regarding this privacy policy you may contact us using the information below:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a href="tel:+971586948688" className="flex items-center hover:text-blue-200 transition-colors">
                <Phone className="h-5 w-5 mr-2" />
                <span>+971586948688</span>
              </a>
              <a href="mailto:business@yallacompare.com" className="flex items-center hover:text-blue-200 transition-colors">
                <Mail className="h-5 w-5 mr-2" />
                <span>business@yallacompare.com</span>
              </a>
              <div className="flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                <span>Dubai, UAE</span>
              </div>
            </div>
            <p className="text-sm text-blue-100 mt-4">
              707 Tameem House, Barsha Heights, Dubai, UAE, P.O. Box 488096
            </p>
          </CardContent>
        </Card>

        {/* CTA Section - tenant-aware bottom banner */}
        <Card className="text-center" style={{ backgroundColor: '#FFFFFF', color: '#111827' }}>
          <CardContent className="pt-8">
            <h3 className="text-2xl font-bold mb-4">Ready to Get Protected?</h3>
            <p className="mb-6">
              Now that you understand how we protect your privacy, let us help protect your business with comprehensive insurance coverage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="hover:opacity-90"
                onClick={() => navigateToPage('/decision-tree')}
              >
                Start Decision Matrix
              </Button>
              <Button
                size="lg"
                className="bg-white text-blue-600 border-white hover:bg-gray-50"
                onClick={() => navigateToPage('/')}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
